import * as styles from "./Input.css";

import { clsx } from "clsx";
import { useCallback, useRef, useState } from "react";

import { Input as BaseInput } from "@base-ui/react/input";
import { Icon } from "@frontend/components/atoms/Icon";
import { useControlContext } from "@frontend/contexts";

import type { InputProps } from "./Input.types";
import { MaskedInput } from "./MaskedInput";

function clearNatively(input: HTMLInputElement) {
  const setter = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    "value",
  )?.set;

  setter?.call(input, "");
  input.dispatchEvent(new Event("input", { bubbles: true }));
}

export function Input({
  size,
  variant,
  invalid,
  success,
  className,
  mask,
  leftSection,
  rightSection,
  clearable = false,
  ref,
  value,
  defaultValue,
  onValueChange,
  ...props
}: InputProps) {
  const inherited = useControlContext();
  const resolvedSize = size ?? inherited.size;

  const inputRef = useRef<HTMLInputElement>(null);
  const [filled, setFilled] = useState(() => Boolean(defaultValue));

  const hasWrapper = Boolean(leftSection || rightSection || clearable);

  const showClear =
    clearable &&
    !props.disabled &&
    (value !== undefined ? value !== "" : filled);

  const trackFilled = (next: string) => {
    setFilled((previous) =>
      (next !== "") === previous ? previous : next !== "",
    );
    onValueChange?.(next);
  };

  const attachInput = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;

      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as { current: HTMLInputElement | null }).current = node;
      }
    },
    [ref],
  );

  const clear = () => {
    const input = inputRef.current;

    if (input) {
      clearNatively(input);
      input.focus();
    }
  };

  const chrome = clsx(
    styles.input({ size: resolvedSize, variant, invalid, success }),
    className,
  );

  const sharedProps = {
    "data-slot": "input" as const,
    className: hasWrapper ? styles.bare : chrome,
    autoComplete: "off" as const,
    "data-1p-ignore": true,
    "aria-invalid": invalid,
    ref: attachInput,
    value,
    defaultValue,
    onValueChange: clearable ? trackFilled : onValueChange,
    ...props,
  };

  const field = mask ? (
    <MaskedInput mask={mask} {...sharedProps} />
  ) : (
    <BaseInput {...sharedProps} />
  );

  if (!hasWrapper) {
    return field;
  }

  return (
    <span data-slot="input-group" className={chrome}>
      {leftSection ? (
        <span className={styles.leftSection}>{leftSection}</span>
      ) : null}

      {field}

      {clearable && !props.disabled ? (
        <button
          type="button"
          data-slot="input-clear"
          className={styles.clear({ visible: showClear })}
          onClick={clear}
          aria-label="Clear"
          tabIndex={showClear ? undefined : -1}
        >
          <Icon name="X" size="sm" />
        </button>
      ) : null}

      {rightSection ? (
        <span className={styles.rightSection}>{rightSection}</span>
      ) : null}
    </span>
  );
}
